import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const WEEKDAY = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Current date / time / weekday in an IANA timezone, e.g. Asia/Kolkata */
function nowIn(tz) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short",
    }).formatToParts(new Date()).map((p) => [p.type, p.value]),
  );
  const hh = parts.hour === "24" ? "00" : parts.hour;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${hh}:${parts.minute}`, weekday: WEEKDAY[parts.weekday] };
}

const seen = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const mark = (key) => { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } };

/**
 * One-click mode: while the admin has the EOD Tracker open in a browser tab, show a
 * nudge (in-app toast + desktop notification) once a day at the reminder time.
 */
export default function ReminderNudge() {
  const navigate = useNavigate();
  const settingsRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    const loadSettings = () => api.get("/settings").then((r) => { settingsRef.current = r.data; }).catch(() => {});

    const check = async () => {
      const s = settingsRef.current;
      if (stopped || !s || s.whatsapp_enabled === false || (s.whatsapp_delivery || "click") !== "click") return;
      const now = nowIn(s.timezone || "Asia/Kolkata");
      if (!s.working_days?.includes(now.weekday)) return;
      if (now.time < (s.whatsapp_reminder_time || "19:00")) return;
      const key = `eod_wa_nudge_${now.date}`;
      if (seen(key)) return;
      mark(key);
      let pending = 0;
      try {
        const { data } = await api.get("/alerts/reminder-links");
        pending = data.items.filter((i) => i.link && i.opted_in && !i.sent_today).length;
      } catch { return; }
      if (!pending) return;
      const open = () => navigate("/?remind=1");
      toast(`⏰ Time to send EOD reminders`, {
        description: `${pending} manager(s) haven't submitted today's EOD.`,
        duration: Infinity,
        action: { label: "Send now", onClick: open },
      });
      if ("Notification" in window && Notification.permission === "granted") {
        const n = new Notification("EOD reminders due", {
          body: `${pending} manager(s) haven't submitted today's EOD. Click to send WhatsApp reminders.`,
        });
        n.onclick = () => { window.focus(); open(); n.close(); };
      }
    };

    loadSettings().then(check);
    const tick = setInterval(check, 60 * 1000);
    const refresh = setInterval(loadSettings, 10 * 60 * 1000);
    return () => { stopped = true; clearInterval(tick); clearInterval(refresh); };
  }, [navigate]);

  return null;
}
